// Chirp: the feed, player posts and their consequences, NPC posts tied to live market
// events, alpha/FUD call resolution, and follower decay (spec section 11).

import { TUNABLES } from "@degen/shared/config/tunables";
import {
  NEWS_TEMPLATES,
  NPC_POST_TEMPLATES,
  PLAYER_POST_TEMPLATES,
  REPLY_TEMPLATES,
  type PlayerPostType,
} from "@degen/shared/config/content";
import type { Rng } from "../rng";
import { pushLog, type GameState } from "../state";
import { addTrust, playerClout, type Player } from "../player";
import { addShill, liveTokens } from "../market/market";
import { balanceOf, mcapOf, priceOf, type Token } from "../market/token";
import { createAccounts, type NpcAccount } from "./npcAccounts";
import type { Giveaway } from "./giveaways";
import { money, moneyShort } from "@degen/shared/money";

const S = TUNABLES.social;

export interface Reply {
  author: string; // "acct:N"
  text: string;
}

export interface Post {
  id: string;
  tick: number;
  author: string; // "player" | "acct:N" | "news"
  type: PlayerPostType | "npc" | "news";
  text: string;
  tokenId?: string;
  collectionId?: string; // NFT drop announcements (engage for WL)
  giveawayId?: string; // giveaway posts (join from the feed)
  targetId?: string; // callout target account or replied-to post id
  likes: number; // final counts; the UI reveals them over the first in-game hour
  reposts: number;
  replies: number;
  replyTexts: Reply[];
  result?: string; // e.g. "Call was right ✅ (+42%)"
}

export interface PendingCall {
  postId: string;
  tokenId: string;
  kind: "alpha" | "fud";
  price0: number;
  resolveTick: number;
  engagement: number;
}

export interface SocialState {
  accounts: NpcAccount[];
  feed: Post[]; // newest last
  nextPostId: number;
  pendingCalls: PendingCall[];
  following: string[]; // accounts the player follows
  playerShills: Record<string, number>; // tokenId -> tick of the last shill
  gmStreak: number;
  lastGmDay: number;
  followerFrac: number; // fractional follower carry so tiny accounts still grow
  logSeen: number; // next log seq to turn into posts
  giveaways: Giveaway[];
}

export function createSocial(rng: Rng): SocialState {
  return {
    accounts: createAccounts(rng),
    feed: [],
    nextPostId: 1,
    pendingCalls: [],
    following: [],
    playerShills: {},
    gmStreak: 0,
    lastGmDay: 0,
    followerFrac: 0,
    logSeen: 0,
    giveaways: [],
  };
}

// ---------------------------------------------------------------- helpers

const fmtMcap = moneyShort;

export function fillTemplate(tpl: string, vars: Record<string, string>): string {
  return tpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? "");
}

function tokenVars(t: Token | undefined, extra: Record<string, string> = {}): Record<string, string> {
  if (!t) return extra;
  const c = t.candles;
  const ago = c.length > 60 ? c[c.length - 61].c : c[0]?.o ?? priceOf(t);
  const pct = ago > 0 ? ((priceOf(t) / ago - 1) * 100).toFixed(0) : "0";
  return { ticker: t.ticker, name: t.name, mcap: fmtMcap(mcapOf(t)), pct: `${pct}%`, ...extra };
}

export function accountById(state: GameState, id: string): NpcAccount | undefined {
  return state.social.accounts[Number(id.split(":")[1])];
}

function addPost(state: GameState, p: Omit<Post, "id" | "tick" | "replyTexts" | "likes" | "reposts" | "replies"> & Partial<Post>): Post {
  const soc = state.social;
  const post: Post = { likes: 0, reposts: 0, replies: 0, replyTexts: [], ...p, id: `p${soc.nextPostId++}`, tick: state.tick };
  soc.feed.push(post);
  if (soc.feed.length > S.feedCap) trimFeed(soc);
  return post;
}

const KEEP_PLAYER_POSTS = 40;

/** Drop the oldest posts, but keep the player's recent posts (call results land on them). */
function trimFeed(soc: SocialState) {
  let excess = soc.feed.length - S.feedCap;
  let mine = 0;
  for (const x of soc.feed) if (x.author === "player") mine++;
  soc.feed = soc.feed.filter((x) => {
    if (excess <= 0) return true;
    if (x.author === "player" && mine <= KEEP_PLAYER_POSTS) return true;
    if (x.author === "player") mine--;
    excess--;
    return false;
  });
}

function npcReplies(state: GameState, rng: Rng, mood: number, n: number): Reply[] {
  const out: Reply[] = [];
  for (let i = 0; i < n; i++) {
    const r = rng.next();
    const pool = r < 0.45 + mood * 0.3 ? REPLY_TEMPLATES.positive : r < 0.75 ? REPLY_TEMPLATES.neutral : REPLY_TEMPLATES.negative;
    out.push({ author: rng.pick(state.social.accounts).id, text: rng.pick(pool) });
  }
  return out;
}

/** Template variations offered in the composer, filled with live data. */
export function postVariants(state: GameState, type: PlayerPostType, tokenId?: string, targetId?: string): string[] {
  const t = tokenId ? state.market.tokens[tokenId] : undefined;
  const target = targetId?.startsWith("acct:") ? accountById(state, targetId)?.handle : undefined;
  return PLAYER_POST_TEMPLATES[type].map((tpl) => fillTemplate(tpl, tokenVars(t, { target: target ?? "anon" })));
}

// ---------------------------------------------------------------- player posts

export interface PostInput {
  postType: PlayerPostType;
  variant: number; // 0..2
  tokenId?: string;
  targetId?: string; // account id for callouts, post id for replies
  text?: string; // freeform post (already moderated)
}

export type PostOutcome = { ok: true; postId: string; followers: number; engagement: number } | { ok: false; error: string };

function gainFollowers(p: Player, soc: SocialState, amount: number): number {
  if (amount > 0) amount /= 1 + p.followers / S.followerSaturation;
  const total = amount + soc.followerFrac;
  const whole = Math.floor(total);
  soc.followerFrac = total - whole;
  p.followers += whole;
  return whole;
}

export function playerPost(state: GameState, rng: Rng, input: PostInput): PostOutcome {
  const p = state.player;
  const soc = state.social;
  const type = input.postType;
  const cfg = S.types[type];
  if (!cfg) return { ok: false, error: "Unknown post type." };
  if (p.daily.layLow) return { ok: false, error: "You're laying low today. No posting." };

  const t = input.tokenId ? state.market.tokens[input.tokenId] : undefined;
  if (type === "alpha" || type === "fud" || type === "shill") {
    if (!t || t.phase === "dead") return { ok: false, error: "Pick a live token." };
    if (type === "shill" && balanceOf(t, "player") <= 0 && t.creator !== "player") return { ok: false, error: "You can only shill a bag you hold." };
  }
  let target: NpcAccount | undefined;
  let replyTo: Post | undefined;
  if (type === "callout") {
    target = input.targetId ? accountById(state, input.targetId) : undefined;
    if (!target) return { ok: false, error: "Pick someone to call out." };
  }
  if (type === "reply") {
    replyTo = soc.feed.find((x) => x.id === input.targetId);
    if (!replyTo || replyTo.author === "player") return { ok: false, error: "Pick a post to reply to." };
  }

  const text = input.text ?? postVariants(state, type, input.tokenId, target?.id)[Math.max(0, Math.min(2, input.variant | 0))];
  const today = p.daily.postTypes[type] ?? 0;
  p.daily.postTypes[type] = today + 1;
  p.daily.posts++;
  p.stats.posts++;

  // --- reach & engagement (spec 11.3) ---
  const sent = state.market.sentiment.value;
  const fit = type === "alpha" || type === "shill" ? 1 + S.sentimentFit * sent : type === "fud" ? 1 - S.sentimentFit * sent : 1;
  let mult = cfg.mult / (1 + S.sameTypeDiminish * today);
  if (type === "gm") {
    if (soc.lastGmDay === p.day - 1) soc.gmStreak++;
    else if (soc.lastGmDay !== p.day) soc.gmStreak = 1;
    soc.lastGmDay = p.day;
    mult *= 1 + S.gmStreakBonusPerDay * Math.min(soc.gmStreak, S.gmStreakMaxDays);
  }
  if (p.flags.comebackUntilDay !== undefined && p.day <= p.flags.comebackUntilDay) mult *= 1.25;
  if (type === "meme" && state.pets.owned.some((x) => x.kind === "frog" && x.happiness >= TUNABLES.pets.perkMinHappiness)) mult *= 1 + TUNABLES.pets.kinds.frog.amount;
  let reach = Math.pow(playerClout(p) + S.baseReach, S.reachExp) * mult * fit * rng.range(0.5, 1.5);
  if (replyTo) {
    const acc = replyTo.author.startsWith("acct:") ? accountById(state, replyTo.author) : undefined;
    reach += Math.sqrt(acc?.followers ?? 100) * S.reply.reachFactor * rng.range(0.5, 1.5);
  }
  const engagement = reach * rng.range(cfg.quality[0], cfg.quality[1]);
  let followers = gainFollowers(p, soc, engagement * S.conversion * (p.trust / 100));

  const post = addPost(state, {
    author: "player",
    type,
    text,
    tokenId: t?.id,
    targetId: target?.id ?? replyTo?.id,
    likes: Math.round(engagement),
    reposts: Math.round(engagement * 0.15),
    replies: Math.round(engagement * 0.05),
  });
  post.replyTexts = npcReplies(state, rng, type === "fud" ? -0.2 : 0.2, rng.int(2, 3));

  // Copytraders follow you only as far as your reach goes; this mirrors npcTraders' scaling.
  const clout = playerClout(p);
  const reachFactor = clout / (clout + 5_000);

  // --- type-specific effects ---
  switch (type) {
    case "alpha":
    case "shill":
      addShill(state.market, { tokenId: t!.id, by: "player", tick: state.tick, strength: 1 });
      t!.hype = Math.min(100, t!.hype + S.call.hypeBoost * reachFactor);
      if (type === "alpha") {
        soc.pendingCalls.push({ postId: post.id, tokenId: t!.id, kind: "alpha", price0: priceOf(t!), resolveTick: state.tick + S.call.resolveTicks, engagement });
      } else {
        soc.playerShills[t!.id] = state.tick;
        if (today + 1 > S.shill.maxPerDay) {
          addTrust(p, S.shill.overLimitTrust);
          pushLog(state, "trust", "Shilling too much. Followers are rolling their eyes. Trust down.");
        }
      }
      break;
    case "fud":
      addShill(state.market, { tokenId: t!.id, by: "player", tick: state.tick, strength: 1, bear: true });
      t!.hype = Math.max(0, t!.hype - S.call.fudHypeHit * reachFactor);
      soc.pendingCalls.push({ postId: post.id, tokenId: t!.id, kind: "fud", price0: priceOf(t!), resolveTick: state.tick + S.call.resolveTicks, engagement });
      break;
    case "thread":
      addTrust(p, S.thread.trust);
      break;
    case "callout": {
      const c = S.callout;
      const winChance = c.winBase + (target!.rugShills > 0 ? c.rugBonus : 0) + (p.trust - 50) * c.trustWeight;
      if (rng.chance(Math.max(0.05, Math.min(0.95, winChance)))) {
        addTrust(p, c.winTrust);
        followers += gainFollowers(p, soc, engagement * S.conversion * c.winFollowersMult);
        target!.followers = Math.round(target!.followers * 0.95);
        post.result = `You won the beef with @${target!.handle} 🏆`;
      } else {
        addTrust(p, c.loseTrust);
        const lost = Math.round(p.followers * c.loseFollowersPct);
        p.followers -= lost;
        followers -= lost;
        post.result = `@${target!.handle} clapped back. You lost this one 💀`;
        post.replyTexts.unshift({ author: target!.id, text: "imagine having no receipts" });
      }
      break;
    }
  }
  if (type === "callout") pushLog(state, "beef", post.result!);
  return { ok: true, postId: post.id, followers, engagement };
}

/** Called by the engine when the player sells: dumping right after shilling costs trust. */
export function onPlayerSell(state: GameState, tokenId: string) {
  const shilledAt = state.social.playerShills[tokenId];
  if (shilledAt !== undefined && state.tick - shilledAt <= S.shill.sellWithinTicks) {
    addTrust(state.player, S.shill.sellAfterShillTrust);
    delete state.social.playerShills[tokenId];
    pushLog(state, "trust", "You sold right after shilling. CT noticed. Trust down.", { tokenId });
  }
}

export function followAccount(state: GameState, id: string): boolean {
  const soc = state.social;
  if (!accountById(state, id)) return false;
  const i = soc.following.indexOf(id);
  if (i >= 0) soc.following.splice(i, 1);
  else soc.following.push(id);
  return true;
}

// ---------------------------------------------------------------- per tick

export function socialTick(state: GameState, rng: Rng) {
  const tick = state.tick;
  const soc = state.social;

  // 1) New log entries (from the market this tick, or player actions since) become posts.
  let first = state.log.length;
  while (first > 0 && state.log[first - 1].seq >= soc.logSeen) first--;
  for (let i = first; i < state.log.length; i++) {
    const e = state.log[i];
    const t = e.data?.tokenId ? state.market.tokens[String(e.data.tokenId)] : undefined;
    if (e.kind === "graduated" && t) addPost(state, { author: "news", type: "news", text: fillTemplate(rng.pick(NEWS_TEMPLATES.graduated), tokenVars(t)), tokenId: t.id, likes: rng.int(50, 400) });
    if (e.kind === "token_spawned" && t && rng.chance(0.5))
      addPost(state, { author: "news", type: "news", text: fillTemplate(rng.pick(NEWS_TEMPLATES.newcoin), tokenVars(t, { pct: `${t.devBagPct}%` })), tokenId: t.id, likes: rng.int(5, 80) });
    if (e.kind === "rug" && t) onRug(state, rng, t);
    const col = e.data?.collection ? state.nft.collections.find((c) => c.id === String(e.data!.collection)) : undefined;
    if (e.kind === "nft_announced" && col)
      addPost(state, { author: "news", type: "news", text: `🖼️ ${col.name} mints tomorrow · ${col.supply.toLocaleString()} supply · ${money(col.mintPrice)} mint. Like + repost for WL 👇`, collectionId: col.id, likes: rng.int(40, 900) });
    if (e.kind === "nft_minted_out" && col) addPost(state, { author: "news", type: "news", text: `🔥 ${col.name} minted out. Floor ${money(col.floor)}`, collectionId: col.id, likes: rng.int(100, 1500) });
    if (e.kind === "nft_failed" && col) addPost(state, { author: "news", type: "news", text: `🥶 ${col.name} failed to mint out. Floor under mint.`, collectionId: col.id, likes: rng.int(20, 300) });
    if (e.kind === "collection_launched" && col)
      addPost(state, { author: "news", type: "news", text: `🎨 @${state.player.handle} just launched ${col.name}. ${col.supply.toLocaleString()} supply at ${money(col.mintPrice)}.`, collectionId: col.id, likes: rng.int(20, 400) });
    if (e.kind === "tge" && t) addPost(state, { author: "news", type: "news", text: `🪂 ${t.name} TGE is live. $${t.ticker} airdropped to farmers. Dump incoming?`, tokenId: t.id, likes: rng.int(200, 2500) });
    if (e.kind === "season2") addPost(state, { author: "news", type: "news", text: `${e.text.split(":")[0]}: "points program season 2 🙏" 💀`, likes: rng.int(500, 5000) });
    if (e.kind === "player_launch" && t) addPost(state, { author: "news", type: "news", text: `🆕 $${t.ticker} (${t.name}) launched by @${state.player.handle}`, tokenId: t.id, likes: rng.int(10, 200) });
    if (e.kind === "player_airdrop" && t) addPost(state, { author: "news", type: "news", text: `🪂 @${state.player.handle} airdropped $${t.ticker} to the community. Based dev?`, tokenId: t.id, likes: rng.int(50, 600) });
    if (e.kind === "exposed") {
      const sleuth = soc.accounts.find((a) => a.personality === "sleuth") ?? soc.accounts[0];
      npcPost(state, rng, sleuth, `🧵 1/ I traced the ${t ? `$${t.ticker}` : "latest"} rug. The dev wallet funded from... @${state.player.handle}. Receipts below. Stay safe out there.`, t?.id);
    }
    if (e.kind === "regime") {
      const pool = e.text.startsWith("Bull") ? NEWS_TEMPLATES.bull : NEWS_TEMPLATES.crash;
      addPost(state, { author: "news", type: "news", text: rng.pick(pool), likes: rng.int(200, 2000) });
    }
  }
  soc.logSeen = state.logSeq;

  // 2) NPC shills placed by the market this tick show up as posts.
  const shills = state.market.shills;
  for (let i = shills.length - 1; i >= 0 && shills[i].tick === tick; i--) {
    const sh = shills[i];
    if (!sh.by.startsWith("acct:")) continue;
    const acc = accountById(state, sh.by);
    const t = state.market.tokens[sh.tokenId];
    if (!acc || !t) continue;
    const pers = acc.personality === "shillbot" ? "shillbot" : "alpha";
    npcPost(state, rng, acc, fillTemplate(rng.pick(NPC_POST_TEMPLATES[pers]), tokenVars(t)), t.id);
  }

  // 3) Ambient NPC chatter, a few posts per in-game hour.
  if (tick % 60 === 0) {
    const n = rng.int(S.npcPostsPerHour[0], S.npcPostsPerHour[1]);
    const live = liveTokens(state.market);
    for (let k = 0; k < n; k++) {
      const acc = rng.pick(soc.accounts);
      const t = live.length ? rng.pick(live) : undefined;
      const tpl = rng.pick(NPC_POST_TEMPLATES[acc.personality]);
      if (tpl.includes("{ticker}") && !t) continue;
      npcPost(state, rng, acc, fillTemplate(tpl, tokenVars(t)), tpl.includes("{ticker}") ? t?.id : undefined);
    }
  }

  // 4) Resolve alpha / FUD calls made 24h ago.
  if (soc.pendingCalls.length && soc.pendingCalls[0].resolveTick <= tick) {
    const due = soc.pendingCalls.filter((c) => c.resolveTick <= tick);
    soc.pendingCalls = soc.pendingCalls.filter((c) => c.resolveTick > tick);
    for (const c of due) resolveCall(state, c);
  }
}

function npcPost(state: GameState, rng: Rng, acc: NpcAccount, text: string, tokenId?: string) {
  const eng = Math.sqrt(acc.followers) * rng.range(0.3, 2);
  addPost(state, {
    author: acc.id,
    type: "npc",
    text,
    tokenId,
    likes: Math.round(eng),
    reposts: Math.round(eng * 0.12),
    replies: Math.round(eng * 0.05),
  });
}

function onRug(state: GameState, rng: Rng, t: Token) {
  addPost(state, { author: "news", type: "news", text: fillTemplate(rng.pick(NEWS_TEMPLATES.rug), tokenVars(t)), tokenId: t.id, likes: rng.int(100, 900) });
  // Every account that shilled this coin is now on the record.
  for (const sh of state.market.shills) if (sh.tokenId === t.id && sh.by.startsWith("acct:")) accountById(state, sh.by)!.rugShills++;
  const sleuths = state.social.accounts.filter((a) => a.personality === "sleuth");
  if (sleuths.length) npcPost(state, rng, rng.pick(sleuths), fillTemplate(NPC_POST_TEMPLATES.sleuth[0], tokenVars(t)), t.id);
}

function resolveCall(state: GameState, c: PendingCall) {
  const p = state.player;
  const t = state.market.tokens[c.tokenId];
  const post = state.social.feed.find((x) => x.id === c.postId);
  const price = t ? (t.phase === "dead" ? 0 : priceOf(t)) : 0;
  const move = c.price0 > 0 ? price / c.price0 - 1 : 0;
  const k = S.call;
  const right = c.kind === "alpha" ? move >= k.alphaWinPct : move <= k.fudWinPct;
  const wrong = c.kind === "alpha" ? move <= k.alphaLosePct : move >= k.fudLosePct;
  const pct = `${move >= 0 ? "+" : ""}${(move * 100).toFixed(0)}%`;
  const ticker = t ? `$${t.ticker}` : "that coin";
  let result: string;
  if (right) {
    addTrust(p, k.trustWin);
    const gained = gainFollowers(p, state.social, c.engagement * S.conversion * k.followerBonusMult);
    result = `Call was right ✅ (${pct}) · +${gained} followers`;
    pushLog(state, "call_right", `Your ${c.kind === "alpha" ? "alpha" : "FUD"} call on ${ticker} was right (${pct}). Trust up.`);
  } else if (wrong) {
    addTrust(p, k.trustLose);
    result = `Call aged badly ❌ (${pct})`;
    pushLog(state, "call_wrong", `Your ${c.kind === "alpha" ? "alpha" : "FUD"} call on ${ticker} aged badly (${pct}). Trust down.`);
  } else {
    result = `Call was meh (${pct})`;
  }
  if (post) post.result = result;
}

// ---------------------------------------------------------------- per day

/** Run at the day boundary BEFORE daily counters reset: decay followers if you didn't post. */
export function socialNewDay(state: GameState) {
  const p = state.player;
  if (p.daily.posts > 0) return;
  const d = S.decay;
  const pct = p.followers >= d.kolThreshold ? d.kolPct : d.smallPct;
  p.followers = Math.max(0, Math.round(p.followers * (1 - pct)));
}
